import dayjs from "dayjs";
import dayjsTimezone from "dayjs/plugin/timezone";
import dayjsUtc from "dayjs/plugin/utc";
import dayjsLocalizedFormat from "dayjs/plugin/localizedFormat";
import dayjsCustomParseFormat from "dayjs/plugin/customParseFormat";
import dayjsDuration from "dayjs/plugin/duration";
import dayjsIsSameOrAfter from "dayjs/plugin/isSameOrAfter";
import dayjsIsSameOrBefore from "dayjs/plugin/isSameOrBefore";

import "dayjs/locale/en";

import { timezoneConfig } from "config/timeZoneConfig";
import { ConfigLocalKey } from "context/configContext";
import { getLocalStorage } from "util/storageUtil";

dayjs.extend(dayjsUtc);
dayjs.extend(dayjsTimezone);
dayjs.extend(dayjsLocalizedFormat);
dayjs.extend(dayjsCustomParseFormat);
dayjs.extend(dayjsDuration);
dayjs.extend(dayjsIsSameOrAfter);
dayjs.extend(dayjsIsSameOrBefore);

// Moment.js compat — plural getter/setter aliases (e.g. .hours() → .hour())
dayjs.extend((_, dayjsClass) => {
  const proto = dayjsClass.prototype;
  const alias = (plural, singular) => {
    proto[plural] = function (val) {
      return val !== undefined ? this[singular](val) : this[singular]();
    };
  };
  alias("years", "year");
  alias("months", "month");
  alias("dates", "date");
  alias("days", "day");
  alias("hours", "hour");
  alias("minutes", "minute");
  alias("seconds", "second");
  alias("milliseconds", "millisecond");
});

dayjs.extend((_, dayjsClass) => {
  const originalFormat = dayjsClass.prototype.format;
  dayjsClass.prototype.format = function (template) {
    const isUtcInstance = this.$u || this.$x?.$timezone === "UTC";
    if (arguments.length === 0 && isUtcInstance) {
      return dayjs.utc(this.valueOf()).format("YYYY-MM-DDTHH:mm:ss[Z]");
    }
    return originalFormat.call(this, template);
  };
});

export const defaultTimezone = timezoneConfig["us"];
export const defaultLocale = "en-US";

const availableLocales = new Set(Object.keys(dayjs.Ls));

const resolveLocale = (locale) => {
  const normalizedLocale = (locale || defaultLocale)
    .toLowerCase()
    .replace(/_/g, "-");
  const localeCandidates = [
    normalizedLocale,
    normalizedLocale.split("-")[0],
    "en",
  ];

  const matchedLocale = localeCandidates.find((code) =>
    availableLocales.has(code),
  );

  return matchedLocale || "en";
};

const invalidMoment = (locale) => dayjs("").locale(locale);

const safeTimezoneParse = (input, timezone) => {
  try {
    return dayjs.tz(input, timezone);
  } catch (error) {
    return dayjs("");
  }
};

export const moment = (
  input = new Date(),
  overridedTimeZone = null,
  overridedLocale = null,
  ignoreTimezone = false
) => {
  let _overridedTimeZone = overridedTimeZone;
  let _overridedLocale = overridedLocale;
  let _ignoreTimezone = ignoreTimezone;

  if (
    overridedTimeZone !== null &&
    typeof overridedTimeZone === "object" &&
    !Array.isArray(overridedTimeZone)
  ) {
    _ignoreTimezone = overridedTimeZone.ignoreTimezone || false;
    _overridedTimeZone = overridedTimeZone.timezone || null;
    _overridedLocale = overridedTimeZone.locale || null;
  }

  const localStorageConfig = getLocalStorage(ConfigLocalKey) || {};
  const { timezone, locale } = localStorageConfig;

  const formatArg = Array.isArray(_overridedTimeZone)
    ? _overridedTimeZone.find((fmt) => dayjs(input, fmt).isValid()) ??
      _overridedTimeZone[0]
    : _overridedTimeZone;

  const _timezone = formatArg || timezone || defaultTimezone;
  const _locale = _overridedLocale || locale || defaultLocale;
  const parseFormat =
    typeof formatArg === "string" &&
    !formatArg.includes("/") &&
    /[YMDHhmsSAa:]/.test(formatArg)
      ? formatArg
      : null;
  const targetTimezone = parseFormat ? timezone || defaultTimezone : _timezone;

  const activeLocale = resolveLocale(_locale);

  // For naive datetime strings, use dayjs.tz(input, tz) so the string is interpreted
  // as being in the configured (airport) timezone rather than the browser's local timezone.
  // This prevents a double-shift when the browser timezone differs from the airport timezone.
  // Date objects and timestamps still use dayjs(input).tz(tz) to correctly convert from UTC.
  // Strings with explicit UTC markers (Z or +/-offset) are parsed as UTC first, then
  // converted to the target timezone — this ensures DST is correctly applied.
  let rawParsed;
  if (parseFormat) {
    const parsed =
      typeof input === "string" ? dayjs(input, parseFormat) : dayjs(input);
    if (!parsed.isValid()) {
      rawParsed = invalidMoment(activeLocale);
    } else {
      rawParsed = _ignoreTimezone
        ? parsed
        : safeTimezoneParse(
            parsed.format("YYYY-MM-DDTHH:mm:ss"),
            targetTimezone,
          );
    }
  } else if (typeof input === "string" && input !== "") {
    // Check if the string carries explicit UTC/offset info (e.g. trailing Z or +05:30).
    // dayjs.tz(string, tz) ignores the Z and treats the value as naive local time,
    // so we must go through dayjs.utc() first to get a correct UTC-to-local conversion.
    const hasTimezoneInfo = /Z$|[+-]\d{2}:?\d{2}$/.test(input.trim());
    if (hasTimezoneInfo) {
      const parsed = dayjs.utc(input);
      rawParsed = parsed.isValid() ? (_ignoreTimezone ? parsed.local() : parsed.tz(targetTimezone)) : invalidMoment(activeLocale);
    } else {
      rawParsed = _ignoreTimezone ? dayjs(input) : safeTimezoneParse(input, targetTimezone);
    }
  } else {
    const parsed = dayjs(input);
    rawParsed = parsed.isValid() ? (_ignoreTimezone ? parsed : parsed.tz(targetTimezone)) : parsed;
  }
  const m = rawParsed.locale(activeLocale);

  if (targetTimezone === "UTC" && !_ignoreTimezone) {
    const originalFormat = m.format;
    m.format = function (template) {
      if (arguments.length === 0) {
        return dayjs.utc(this.valueOf()).format("YYYY-MM-DDTHH:mm:ss[Z]");
      }
      return originalFormat.call(this, template);
    };
  }

  m.customFormat = () => {
    if (
      activeLocale === "en" &&
      (targetTimezone === "CET" ||
        targetTimezone === "Europe/Berlin" ||
        targetTimezone === "Europe/Paris")
    ) {
      return m.format("DD MMM, YYYY HH:mm");
    }
    return m.format("lll");
  };

  return m;
};

moment.utc = (...args) => dayjs.utc(...args);
moment.tz = (...args) => {
  try {
    return dayjs.tz(...args);
  } catch (error) {
    return dayjs("");
  }
};
moment.duration = (...args) => dayjs.duration(...args);
// Export the configured dayjs instance for files that need static methods (.utc, .tz)
export { dayjs };
export default moment;
